import { FunctionComponent, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Avatar,
    Badge,
    Box,
    Button,
    Checkbox,
    Divider,
    Flex,
    FormControl,
    FormHelperText,
    FormLabel,
    Heading,
    Input,
    Spinner,
    Text,
    Textarea,
    Tooltip,
    useClipboard,
    useDisclosure,
    VStack,
} from "@chakra-ui/react";
import { IconAlertTriangle, IconArrowLeft, IconDatabase, IconSettings, IconTrash, IconUsers } from "@tabler/icons-react";
import { TenantResponse, useDeleteTenant, useTenant, useUpdateTenant } from "../../api";
import { CandidateFieldsConfiguration } from "../../api/models/TenantResponse";
import { useUserContext } from "../../context/UserContext";
import { SetPageTitle } from "../../components/SetPageTitle";
import { ConfirmationDialog } from "../../components/ConfirmationDialog";
import { formatTimestampStr } from "@am-tax/fe-core";
import { Controller, useForm } from "react-hook-form";
import { createPatch } from "rfc6902";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";

type ClientSection = "settings" | "custom-fields" | "system" | "danger";

const avatarColors = ["blue", "teal", "green", "purple", "orange", "pink", "cyan", "red"];

function getAvatarColor(name: string): string {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return avatarColors[Math.abs(hash) % avatarColors.length];
}

export const AdminClientDetailPage: FunctionComponent = () => {
    const { tenantId, section } = useParams<{ tenantId: string; section: string }>();
    const navigate = useNavigate();
    const appInsights = useAppInsightsContext();
    const activeSection: ClientSection = (section as ClientSection) ?? "settings";

    const tenantQuery = useTenant(tenantId);
    const tenant = tenantQuery.data;

    useEffect(() => {
        appInsights.trackPageView({ uri: `/admin/clients/${tenantId}` });
    }, [appInsights, tenantId]);

    const avatarColor = getAvatarColor(tenant?.name ?? "");
    const initials = (tenant?.name ?? "?").slice(0, 2).toUpperCase();

    const navItems: { id: ClientSection; label: string; icon: React.ReactNode; danger?: boolean }[] = [
        { id: "settings", label: "Settings", icon: <IconSettings size={16} /> },
        { id: "custom-fields", label: "Custom Fields", icon: <IconDatabase size={16} /> },
        { id: "system", label: "System", icon: <IconUsers size={16} /> },
        { id: "danger", label: "Danger Zone", icon: <IconAlertTriangle size={16} />, danger: true },
    ];

    return (
        <Box p={8} maxW="1000px" mx="auto">
            <SetPageTitle title={tenant?.name ? `${tenant.name} — Client` : "Client Detail"} />

            <Button
                variant="ghost"
                size="sm"
                leftIcon={<IconArrowLeft size={16} />}
                onClick={() => navigate("/admin/clients")}
                mb={6}
                color="gray.600"
                _hover={{ bg: "gray.100" }}
            >
                Back to Clients
            </Button>

            <Flex gap={10} align="flex-start">
                {/* Left sidebar */}
                <Box w="240px" flexShrink={0} position="sticky" top={8}>
                    <VStack spacing={3} align="center" mb={6}>
                        <Avatar size="xl" name={tenant?.name ?? "?"} bg={`${avatarColor}.500`} color="white" getInitials={() => initials} />
                        <VStack spacing={1} align="center">
                            <Text fontWeight="semibold" fontSize="md" textAlign="center">
                                {tenant?.name ?? "—"}
                            </Text>
                            {tenant?.externalId && (
                                <Text fontSize="xs" color="gray.500" fontFamily="mono">
                                    {tenant.externalId}
                                </Text>
                            )}
                            {tenant && (
                                <Badge colorScheme={tenant.isEnabled ? "green" : "red"} borderRadius="full">
                                    {tenant.isEnabled ? "Enabled" : "Disabled"}
                                </Badge>
                            )}
                        </VStack>
                    </VStack>

                    <Divider mb={3} />

                    <VStack spacing={1} align="stretch">
                        {navItems.map(item => {
                            const isActive = activeSection === item.id;
                            const activeColor = item.danger ? "red" : "blue";
                            return (
                                <Flex
                                    key={item.id}
                                    as="button"
                                    onClick={() => navigate(`/admin/clients/${tenantId}/${item.id}`)}
                                    align="center"
                                    gap={2.5}
                                    px={3}
                                    py={2}
                                    borderRadius="md"
                                    borderLeft={isActive ? "3px solid" : "3px solid transparent"}
                                    borderColor={isActive ? `${activeColor}.500` : "transparent"}
                                    bg={isActive ? `${activeColor}.50` : "transparent"}
                                    color={isActive ? `${activeColor}.700` : item.danger ? "red.600" : "gray.700"}
                                    fontWeight={isActive ? "semibold" : "normal"}
                                    fontSize="sm"
                                    textAlign="left"
                                    transition="all 0.15s"
                                    _hover={{ bg: isActive ? `${activeColor}.50` : item.danger ? "red.50" : "gray.100" }}
                                    w="full"
                                >
                                    <Box flexShrink={0} color={isActive ? `${activeColor}.500` : item.danger ? "red.500" : "gray.500"}>
                                        {item.icon}
                                    </Box>
                                    {item.label}
                                </Flex>
                            );
                        })}
                    </VStack>
                </Box>

                {/* Right content */}
                <Box flex={1} minW={0}>
                    {tenantQuery.isLoading ? (
                        <Spinner />
                    ) : !tenant ? (
                        <Text color="gray.500">Client not found.</Text>
                    ) : (
                        <>
                            {activeSection === "settings" && <ClientSettingsSection tenant={tenant} />}
                            {activeSection === "custom-fields" && <ClientCustomFieldsSection tenant={tenant} />}
                            {activeSection === "system" && <ClientSystemSection tenant={tenant} />}
                            {activeSection === "danger" && tenantId && (
                                <ClientDangerSection tenant={tenant} tenantId={tenantId} onDelete={() => navigate("/admin/clients")} />
                            )}
                        </>
                    )}
                </Box>
            </Flex>
        </Box>
    );
};

const SectionHeader: FunctionComponent<{ title: string; description?: string }> = ({ title, description }) => (
    <Box mb={6}>
        <Heading size="md" mb={1}>
            {title}
        </Heading>
        {description && (
            <Text fontSize="sm" color="gray.500">
                {description}
            </Text>
        )}
        <Divider mt={4} />
    </Box>
);

const FieldRow: FunctionComponent<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <Box>
        <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" color="gray.500" mb={1}>
            {label}
        </Text>
        {children}
    </Box>
);

const ClientSettingsSection: FunctionComponent<{ tenant: TenantResponse }> = ({ tenant }) => {
    const formValues = useMemo(
        () => ({
            name: tenant.name,
            description: tenant.description ?? "",
            isEnabled: tenant.isEnabled,
            externalId: tenant.externalId ?? "",
        }),
        [tenant]
    );

    const updateTenant = useUpdateTenant();
    const {
        control,
        setValue,
        reset,
        formState: { isDirty },
        register,
        handleSubmit,
    } = useForm();

    useEffect(() => {
        Object.entries(formValues).forEach(([key, value]) => {
            // @ts-ignore
            setValue(key, value);
        });
    }, [formValues, setValue]);

    return (
        <Box>
            <SectionHeader title="Settings" description="Basic client information and availability." />
            <form
                onSubmit={handleSubmit(data => {
                    const patch = createPatch(formValues, data);
                    updateTenant.mutateAsync({ tenantId: tenant.id, patch }).then(response => {
                        reset({
                            name: response.name,
                            description: response.description ?? "",
                            isEnabled: response.isEnabled,
                            externalId: response.externalId ?? "",
                        });
                    });
                })}
            >
                <VStack spacing={5} align="stretch" maxW="500px">
                    <Controller
                        control={control}
                        name="isEnabled"
                        render={({ field }) => (
                            <FormControl>
                                <FormLabel>Status</FormLabel>
                                <Checkbox isChecked={field.value} onChange={field.onChange}>
                                    Enabled
                                </Checkbox>
                                {field.value === false && <FormHelperText>Disabled clients are not visible to users.</FormHelperText>}
                            </FormControl>
                        )}
                    />
                    <FormControl>
                        <FormLabel>Name</FormLabel>
                        <Input placeholder="Client name" {...register("name")} />
                    </FormControl>
                    <FormControl>
                        <FormLabel>Description</FormLabel>
                        <Textarea placeholder="Client description" {...register("description")} />
                    </FormControl>
                    <FormControl>
                        <FormLabel>External ID</FormLabel>
                        <Input placeholder="Optional external identifier" {...register("externalId")} />
                    </FormControl>
                    <Flex justify="flex-end">
                        <Button colorScheme="blue" type="submit" isDisabled={!isDirty || updateTenant.isLoading} isLoading={updateTenant.isLoading}>
                            Save changes
                        </Button>
                    </Flex>
                </VStack>
            </form>
        </Box>
    );
};

const ClientCustomFieldsSection: FunctionComponent<{ tenant: TenantResponse }> = ({ tenant }) => {
    const tenantFormValues = useMemo(
        () => ({
            customField01: tenant.customField01 ?? "",
            customField02: tenant.customField02 ?? "",
            customField03: tenant.customField03 ?? "",
            customField04: tenant.customField04 ?? "",
            customField05: tenant.customField05 ?? "",
        }),
        [tenant]
    );

    const updateTenant = useUpdateTenant();
    const {
        setValue: setTenantValue,
        reset: resetTenant,
        formState: { isDirty: isTenantDirty },
        register: registerTenant,
        handleSubmit: handleTenantSubmit,
    } = useForm();

    useEffect(() => {
        Object.entries(tenantFormValues).forEach(([key, value]) => {
            // @ts-ignore
            setTenantValue(key, value);
        });
    }, [tenantFormValues, setTenantValue]);

    const cfg = tenant.candidateFieldsConfiguration;
    const candidateFormValues = useMemo(
        (): CandidateFieldsConfiguration => ({
            customField01DisplayName: cfg?.customField01DisplayName ?? "",
            isCustomField01ShownInGrid: cfg?.isCustomField01ShownInGrid ?? false,
            customField02DisplayName: cfg?.customField02DisplayName ?? "",
            isCustomField02ShownInGrid: cfg?.isCustomField02ShownInGrid ?? false,
            customField03DisplayName: cfg?.customField03DisplayName ?? "",
            isCustomField03ShownInGrid: cfg?.isCustomField03ShownInGrid ?? false,
            customField04DisplayName: cfg?.customField04DisplayName ?? "",
            isCustomField04ShownInGrid: cfg?.isCustomField04ShownInGrid ?? false,
            customField05DisplayName: cfg?.customField05DisplayName ?? "",
            isCustomField05ShownInGrid: cfg?.isCustomField05ShownInGrid ?? false,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [tenant]
    );

    const updateCandidateFields = useUpdateTenant();
    const {
        setValue: setCandidateValue,
        reset: resetCandidate,
        formState: { isDirty: isCandidateDirty },
        register: registerCandidate,
        control: candidateControl,
        handleSubmit: handleCandidateSubmit,
    } = useForm<{ candidateFieldsConfiguration: CandidateFieldsConfiguration }>({
        defaultValues: { candidateFieldsConfiguration: candidateFormValues },
    });

    useEffect(() => {
        Object.entries(candidateFormValues).forEach(([key, value]) => {
            setCandidateValue(`candidateFieldsConfiguration.${key}` as any, value);
        });
    }, [candidateFormValues, setCandidateValue]);

    return (
        <Box>
            <SectionHeader title="Integration Custom Fields" description="Arbitrary data fields for integration or organizational purposes." />
            <form
                onSubmit={handleTenantSubmit(data => {
                    const patch = createPatch(tenantFormValues, data);
                    updateTenant.mutateAsync({ tenantId: tenant.id, patch }).then(response => {
                        resetTenant({
                            customField01: response.customField01 ?? "",
                            customField02: response.customField02 ?? "",
                            customField03: response.customField03 ?? "",
                            customField04: response.customField04 ?? "",
                            customField05: response.customField05 ?? "",
                        });
                    });
                })}
            >
                <VStack spacing={4} align="stretch" maxW="500px">
                    <Input placeholder="Custom field 1" {...registerTenant("customField01")} />
                    <Input placeholder="Custom field 2" {...registerTenant("customField02")} />
                    <Input placeholder="Custom field 3" {...registerTenant("customField03")} />
                    <Input placeholder="Custom field 4" {...registerTenant("customField04")} />
                    <Input placeholder="Custom field 5" {...registerTenant("customField05")} />
                    <Flex justify="flex-end">
                        <Button colorScheme="blue" type="submit" isDisabled={!isTenantDirty || updateTenant.isLoading} isLoading={updateTenant.isLoading}>
                            Save changes
                        </Button>
                    </Flex>
                </VStack>
            </form>

            <Divider my={8} />

            <SectionHeader title="Candidate Custom Fields" description="Configure display names and grid visibility for candidate custom fields." />
            <form
                onSubmit={handleCandidateSubmit(data => {
                    const patch = createPatch({ candidateFieldsConfiguration: candidateFormValues }, data);
                    updateCandidateFields.mutateAsync({ tenantId: tenant.id, patch }).then(response => {
                        const updated = response.candidateFieldsConfiguration;
                        resetCandidate({
                            candidateFieldsConfiguration: {
                                customField01DisplayName: updated?.customField01DisplayName ?? "",
                                isCustomField01ShownInGrid: updated?.isCustomField01ShownInGrid ?? false,
                                customField02DisplayName: updated?.customField02DisplayName ?? "",
                                isCustomField02ShownInGrid: updated?.isCustomField02ShownInGrid ?? false,
                                customField03DisplayName: updated?.customField03DisplayName ?? "",
                                isCustomField03ShownInGrid: updated?.isCustomField03ShownInGrid ?? false,
                                customField04DisplayName: updated?.customField04DisplayName ?? "",
                                isCustomField04ShownInGrid: updated?.isCustomField04ShownInGrid ?? false,
                                customField05DisplayName: updated?.customField05DisplayName ?? "",
                                isCustomField05ShownInGrid: updated?.isCustomField05ShownInGrid ?? false,
                            },
                        });
                    });
                })}
            >
                <VStack spacing={4} align="stretch" maxW="600px">
                    {([1, 2, 3, 4, 5] as const).map(n => {
                        const num = n.toString().padStart(2, "0");
                        return (
                            <Flex key={n} gap={4} align="center">
                                <FormControl flex={1}>
                                    <FormLabel fontSize="sm">Field {n} Display Name</FormLabel>
                                    <Input
                                        placeholder={`Custom field ${n} label`}
                                        {...registerCandidate(`candidateFieldsConfiguration.customField${num}DisplayName` as any)}
                                    />
                                </FormControl>
                                <FormControl width="auto" pt={6}>
                                    <Controller
                                        control={candidateControl}
                                        name={`candidateFieldsConfiguration.isCustomField${num}ShownInGrid` as any}
                                        render={({ field }) => (
                                            <Checkbox isChecked={!!field.value} onChange={field.onChange}>
                                                Show in grid
                                            </Checkbox>
                                        )}
                                    />
                                </FormControl>
                            </Flex>
                        );
                    })}
                    <Flex justify="flex-end">
                        <Button
                            colorScheme="blue"
                            type="submit"
                            isDisabled={!isCandidateDirty || updateCandidateFields.isLoading}
                            isLoading={updateCandidateFields.isLoading}
                        >
                            Save changes
                        </Button>
                    </Flex>
                </VStack>
            </form>
        </Box>
    );
};

const ClientSystemSection: FunctionComponent<{ tenant: TenantResponse }> = ({ tenant }) => {
    const { dateFormat } = useUserContext();
    const idClipboard = useClipboard(tenant.id);

    return (
        <Box>
            <SectionHeader title="System" description="Read-only system metadata and database connection information." />
            <VStack spacing={6} align="stretch">
                <FieldRow label="Client ID">
                    <Tooltip label={idClipboard.hasCopied ? "Copied!" : "Click to copy"} placement="right">
                        <Text fontSize="sm" fontFamily="mono" color="gray.600" cursor="copy" onClick={idClipboard.onCopy} display="inline-block">
                            {tenant.id}
                        </Text>
                    </Tooltip>
                </FieldRow>
                <FieldRow label="Created by">
                    <Text fontSize="sm">{tenant.createdByDisplayName ?? "—"}</Text>
                </FieldRow>
                <FieldRow label="Created on">
                    <Text fontSize="sm">{formatTimestampStr(tenant.createdOn, dateFormat)}</Text>
                </FieldRow>
                <FieldRow label="Updated by">
                    <Text fontSize="sm">{tenant.updatedByDisplayName ?? "—"}</Text>
                </FieldRow>
                <FieldRow label="Updated on">
                    <Text fontSize="sm">{tenant.updatedOn ? formatTimestampStr(tenant.updatedOn, dateFormat) : "—"}</Text>
                </FieldRow>
                <Divider />
                <FieldRow label="Database ID">
                    <Text fontSize="sm" fontFamily="mono">
                        {tenant.databaseId ?? "—"}
                    </Text>
                </FieldRow>
                <FieldRow label="Database Name">
                    <Text fontSize="sm" fontFamily="mono">
                        {tenant.databaseName ?? "—"}
                    </Text>
                </FieldRow>
                <FieldRow label="Database Server">
                    <Text fontSize="sm" fontFamily="mono">
                        {tenant.databaseServer ?? "—"}
                    </Text>
                </FieldRow>
            </VStack>
        </Box>
    );
};

const ClientDangerSection: FunctionComponent<{ tenant: TenantResponse; tenantId: string; onDelete: () => void }> = ({ tenant, tenantId, onDelete }) => {
    const deleteTenant = useDeleteTenant();
    const deleteDialog = useDisclosure();

    return (
        <Box>
            <SectionHeader title="Danger Zone" description="Irreversible actions that affect this client and all associated data." />
            <Box borderWidth="1px" borderColor="red.200" borderRadius="md" p={4}>
                <Flex align="center" gap={4} justify="space-between">
                    <Box>
                        <Text fontWeight="medium" fontSize="sm">
                            Delete this client
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                            This will remove all access and prevent future operations.
                        </Text>
                    </Box>
                    <Button colorScheme="red" variant="outline" size="sm" leftIcon={<IconTrash size={14} />} onClick={deleteDialog.onOpen}>
                        Delete client
                    </Button>
                </Flex>
            </Box>

            <ConfirmationDialog
                isOpen={deleteDialog.isOpen}
                onClose={deleteDialog.onClose}
                title="Delete Client"
                confirmLabel="Delete"
                confirmColorScheme="red"
                isDisabled={deleteTenant.isLoading}
                onConfirm={() => {
                    deleteTenant.mutate(tenantId);
                    onDelete();
                }}
            >
                <p>
                    This will remove all access to <strong>{tenant.name}</strong> and prevent any future operations.
                </p>
                <br />
                <p>
                    Are you <em>sure</em>?
                </p>
            </ConfirmationDialog>
        </Box>
    );
};
